import { DisplayObject, Graphics, Point, Rectangle } from "pixi.js";
import App from "../../../vendor_core/core/src/App";
import GameModel from "../../../vendor_core/core/src/model/GameModel";
import Layout from "../../../vendor_core/core/src/utils/Layout";

/**
 * Provides access to helper methods, related to display object bounds.
 */
export abstract class BoundUtils {
    public static isOutOfBounds(bounds: Rectangle, frame: Rectangle): boolean {
        if (bounds.top < frame.top) {
            return true;
        }

        if (bounds.bottom > frame.bottom) {
            return true;
        }

        if (bounds.left < frame.left) {
            return true;
        }

        if (bounds.right > frame.right) {
            return true;
        }

        return false;
    }

    /**
     * Moves the objectToMove (if nessesary) inside the frame.
     * @param objectToMove The object, whose bounds must be within the passed frame.
     * @param frame The frame.
     */
    public static ensureInBounds(
        objectToMove: any,
        frame: Rectangle,
        scalar: { upScaleMultiplier; upScaleMultiplierY }
    ) {
        let bounds = objectToMove.getBounds();
        if (!GameModel.isMobile() || (GameModel.isMobile() && !Layout.isLandscape())) {
            bounds.x += objectToMove.children[1].x;
        } 
        bounds.y += objectToMove.height / 2;

        BoundUtils.scaleBounds(
            bounds,
            scalar.upScaleMultiplier,
            scalar.upScaleMultiplierY
        );

        if (BoundUtils.isOutOfBounds(bounds, frame)) {
            let displacement = BoundUtils.computeTranslateToEnsureBounds(
                bounds,
                frame
            );

            objectToMove.position.set(
                objectToMove.position.x + displacement.x,
                objectToMove.position.y + displacement.y
            );
        }
    }

    public static ensureRectInReelBounds(bounds: Rectangle, frame: Rectangle) {
        // Decreasing the reel bounds
        //frameBounds.pad(0, -bounds.height / 2);

        if (bounds.width > frame.width || bounds.height > frame.height) {
            //throw Error("object too big");
        }

        let displacement = BoundUtils.computeTranslateToEnsureBounds(
            bounds,
            frame
        );
        bounds.x += displacement.x;
        bounds.y += displacement.y;

        return { x: displacement.x, y: displacement.y };
    }

    /**
     * Computes how many pixels the passed rect should be moved in order to fit the frame inner bounds.
     * @param rect The rect, whose bounds must fit the frame.
     * @param frame The frame, where the rect should fit.
     * @returns The x and y displacement values, packed in a pixi point object.
     */
    public static computeTranslateToEnsureBounds(
        rect: Rectangle,
        frame: Rectangle
    ): Point {
        if (rect.width > frame.width || rect.height > frame.height) {
            //throw Error("object too big");
        }

        let point = new Point(0, 0);

        // Check top
        if (rect.top < frame.top) {
            point.y += Math.ceil(frame.top - rect.top);
        }

        // Check bottom
        if (rect.bottom > frame.bottom) {
            point.y -= Math.ceil(rect.bottom - frame.bottom);
        }

        // Check left
        if (rect.left < frame.left) {
            point.x += Math.ceil(frame.left - rect.left);
        }

        // Check right
        if (rect.right > frame.right) {
            point.x -= Math.ceil(rect.right - frame.right);
        }

        return point;
    }

    /**
     * Scales rectangle from its center by provided multiplier
     * @param bound the bound to scale
     * @param scalar multiplier
     */
    public static scaleBounds(
        bound: Rectangle,
        scalarX: number,
        scalarY: number = null
    ) {
        if (!scalarY) {
            bound.width *= scalarX;
            bound.height *= scalarX;

            let xDifference = (bound.width - bound.width / scalarX) / 2;
            let yDifference = (bound.height - bound.height / scalarX) / 2;
            bound.x -= xDifference;
            bound.y -= yDifference;
            return;
        }

        bound.width *= scalarX;
        bound.height *= scalarY;

        let xDifference = (bound.width - bound.width / scalarX) / 2;
        let yDifference = (bound.height - bound.height / scalarY) / 2;
        bound.x -= xDifference;
        bound.y -= yDifference;
    }

    /**
     * Gets movement distance of all four sides where object can be moved
     * @param bounds1 
     * @param bounds2 
     * @param multiplier 
     * @returns 
     */
    private static getMovementDirections(bounds1: Rectangle, bounds2: Rectangle, multiplier: number): {
        moveToBottomDistance: number,
        moveToLeftDistance: number,
        moveToRightDistance: number,
        moveToTopDistance: number,
        movementArray: Array<number>
    } {
        let moveToLeftDistance = null;
        let moveToRightDistance = null;

        let moveToTopDistance = Math.ceil(
            (bounds1.bottom - bounds2.top) * multiplier
        );
        let moveToBottomDistance = Math.ceil(
            (bounds2.bottom - bounds1.top) * multiplier
        );

        let movementArray = [];

        moveToLeftDistance = Math.ceil(
            (bounds1.right - bounds2.left) * multiplier
        );
        moveToRightDistance = Math.ceil(
            (bounds2.right - bounds1.left) * multiplier
        );

        movementArray = [
            moveToLeftDistance,
            moveToRightDistance,
            moveToTopDistance,
            moveToBottomDistance,
        ];

        movementArray.sort((a, b) => a - b);

        return {
            movementArray,
            moveToLeftDistance,
            moveToRightDistance,
            moveToTopDistance,
            moveToBottomDistance,
        };
    }

    /**
     * Returns shortest distnace where object can be moved without going outside of the reel bounds and overlapping something else
     * @param bounds 
     * @param movementArray 
     * @param moveToLeftDistance 
     * @param moveToRightDistance 
     * @param moveToTopDistance 
     * @param moveToBottomDistance 
     * @param reelBounds 
     * @param restrictedZone 
     * @returns 
     */
    private static getShortestDistance(
        bounds: Rectangle,
        movementArray: Array<number>,
        moveToLeftDistance: number,
        moveToRightDistance: number,
        moveToTopDistance: number,
        moveToBottomDistance: number,
        reelBounds: Rectangle,
        restrictedZone: Rectangle
    ): number {
        let boundsStartingPos = {
            x: bounds.x,
            y: bounds.y,
        };
        let correctDisplacement = false;
        for (let i = 0; i < movementArray.length; i++) {
            correctDisplacement = false;
            if (moveToLeftDistance == movementArray[i]) {
                bounds.x -= moveToLeftDistance;
            }

            if (moveToRightDistance == movementArray[i]) {
                bounds.x += moveToRightDistance;
            }

            if (moveToTopDistance == movementArray[i]) {
                bounds.y -= moveToTopDistance;
            }

            if (moveToBottomDistance == movementArray[i]) {
                bounds.y += moveToBottomDistance;
            }

            if (BoundUtils.isOutOfBounds(bounds, reelBounds)) {
                bounds.x = boundsStartingPos.x;
                bounds.y = boundsStartingPos.y;
                continue;
            } else {
                correctDisplacement = true;
            }

            if (restrictedZone != null && correctDisplacement) {
                if (BoundUtils.intersects(bounds, restrictedZone)) {
                    correctDisplacement = false;
                    break;
                }
            }

            if (correctDisplacement) {
                return movementArray[i];
            }

            bounds.x = boundsStartingPos.x;
            bounds.y = boundsStartingPos.y;
        }

        return null;
    }

    /**
     * Moves the objectToMove (if nessesary) outside the frame.
     * @param objectToMove The object, whose bounds must be outside the passed frame.
     * @param frameBounds The frame.
     * @returns True if the objectToMove has been moved, otherwise false.
     */
    public static ensureOutOfBounds(
        objectToMove: any,
        frameBounds: Rectangle,
        objectToMoveScalar: { upScaleMultiplier; upScaleMultiplierY },
        frameScalar: any,
        reelBounds: Rectangle,
        restrictedZone: Rectangle = null,
        moveFrame: boolean = false
    ): { x: number; y: number } {
        let bounds = objectToMove.getBounds();

        //setting correct X position of the bounds
        if (!GameModel.isMobile() || (GameModel.isMobile() && !Layout.isLandscape())) {
            bounds.x += objectToMove.children[1].x;
        } 
        bounds.y += objectToMove.height / 2;

        if (moveFrame) {
            if (!GameModel.isMobile() || (GameModel.isMobile() && !Layout.isLandscape())) {
                frameBounds.x += objectToMove.children[1].x;
            } 
            frameBounds.y += objectToMove.height / 2;
        }
        BoundUtils.scaleBounds(
            bounds,
            objectToMoveScalar.upScaleMultiplier,
            objectToMoveScalar.upScaleMultiplierY
        );

        if (typeof frameScalar == "number") {
            BoundUtils.scaleBounds(frameBounds, frameScalar);
        } else {
            BoundUtils.scaleBounds(
                frameBounds,
                frameScalar.upScaleMultiplier,
                frameScalar.upScaleMultiplierY
            );
        }

        if (!BoundUtils.intersects(bounds, frameBounds)) {
            return { x: null, y: null };
        }

        // We will measure the distances, needed to move the object outside the frame.
        let multiplier = 1.0;
        if (GameModel.isMobile() && !Layout.isLandscape()) {
            multiplier = 0.4;
        } else if (GameModel.isMobile() && Layout.isLandscape()) {
            multiplier = 1;
        }

        let shortest = null;
        const {
            moveToBottomDistance,
            moveToLeftDistance,
            moveToRightDistance,
            moveToTopDistance,
            movementArray,
        } = BoundUtils.getMovementDirections(bounds, frameBounds, multiplier);
        shortest = BoundUtils.getShortestDistance(
            bounds,
            movementArray,
            moveToLeftDistance,
            moveToRightDistance,
            moveToTopDistance,
            moveToBottomDistance,
            reelBounds,
            restrictedZone
        );

        return BoundUtils.moveObject(
            objectToMove,
            moveToLeftDistance,
            moveToRightDistance,
            moveToTopDistance,
            moveToBottomDistance,
            shortest
        );
    }

    public static moveObject(
        objectToMove,
        moveToLeftDistance,
        moveToRightDistance,
        moveToTopDistance,
        moveToBottomDistance,
        shortest
    ) {
        if (moveToLeftDistance != null && moveToLeftDistance == shortest) {
            // Move to left
            return {
                x: objectToMove.position.x - moveToLeftDistance,
                y: objectToMove.position.y,
            };
        }

        if (moveToRightDistance != null && moveToRightDistance == shortest) {
            // Move to right

            return {
                x: objectToMove.position.x + moveToRightDistance,
                y: objectToMove.position.y,
            };
        }

        if (moveToTopDistance == shortest) {
            // Move to top

            return {
                x: objectToMove.position.x,
                y: objectToMove.position.y - moveToTopDistance,
            };
        }

        if (moveToBottomDistance == shortest) {
            // Move to bottom

            return {
                x: objectToMove.position.x,
                y: objectToMove.position.y + moveToBottomDistance,
            };
        }

        return { x: null, y: null };
    }

    /**
     * Checks whether two rects intersects each other.
     *
     * @private
     * @param {Rectangle} rect1
     * @param {Rectangle} rect2
     * @return {*}  {boolean}
     * @memberof BBWinlinesView
     */
    public static intersects(rect1: Rectangle, rect2: Rectangle): boolean {
        return (
            rect1.x + rect1.width > rect2.x &&
            rect1.x < rect2.x + rect2.width &&
            rect1.y + rect1.height > rect2.y &&
            rect1.y < rect2.y + rect2.height
        );
    }

    /**
     * Computes the composite axis alined bounding box of the passed rectangles.
     * @param rects The rects, whose composite bound to compute.
     * @returns The composite AABB of the passed rects.
     */
    public static createCompositeBound(
        rects: Array<Rectangle>,
        scalar: number
    ): Rectangle {
        let composite: Rectangle;
        for (let i = 0; i < rects.length; i++) {
            const rect = rects[i];
            BoundUtils.scaleBounds(rect, scalar);
            if (composite) {
                composite.enlarge(rect);
            } else {
                composite = rect;
            }
        }

        return composite;
    }
}
